import React, { useState } from 'react';
import type { IdentityValueScoreProps, ScoreFactors } from './types';

// ─── Score range helpers ──────────────────────────────────────────────────────

interface RangeConfig {
  label: string;
  color: string;       // stroke / fill color class
  textColor: string;   // numeric text color
  bgColor: string;     // badge bg
  borderColor: string; // badge border
}

function getRange(score: number): RangeConfig {
  if (score <= 30) {
    return {
      label: 'Low',
      color: 'text-danger-500',
      textColor: 'text-danger-700',
      bgColor: 'bg-danger-50',
      borderColor: 'border-danger-300',
    };
  }
  if (score <= 60) {
    return {
      label: 'Medium',
      color: 'text-warning-500',
      textColor: 'text-warning-700',
      bgColor: 'bg-warning-50',
      borderColor: 'border-warning-300',
    };
  }
  if (score <= 80) {
    return {
      label: 'High',
      color: 'text-brand-500',
      textColor: 'text-brand-700',
      bgColor: 'bg-brand-50',
      borderColor: 'border-brand-300',
    };
  }
  return {
    label: 'Top Value',
    color: 'text-success-500',
    textColor: 'text-success-700',
    bgColor: 'bg-success-50',
    borderColor: 'border-success-300',
  };
}

const STAR_ICON = (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true" className="shrink-0">
    <path d="M6 1l1.3 2.7 3 .4-2.2 2.1.5 3L6 7.8 3.4 9.2l.5-3L1.7 4.1l3-.4L6 1z" />
  </svg>
);

// ─── Factor tooltip content ───────────────────────────────────────────────────

function FactorBreakdown({ factors }: { factors: ScoreFactors }) {
  const rows: [string, number][] = [
    ['Spend', factors.spend],
    ['Frequency', factors.frequency],
    ['Recency', factors.recency],
    ['Loyalty', factors.loyalty],
    ['Credit repayment', factors.creditRepayment],
  ];

  return (
    <div className="space-y-1 min-w-[160px]">
      <p className="text-xs font-semibold text-white mb-1">Score breakdown</p>
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3">
          <span className="text-xs text-neutral-300">{label}</span>
          <div className="flex items-center gap-1">
            <div className="w-16 h-1.5 rounded-full bg-neutral-600 overflow-hidden">
              <div
                className="h-full rounded-full bg-white"
                style={{ width: `${Math.min(100, value)}%` }}
              />
            </div>
            <span className="text-xs text-white tabular-nums w-5 text-right">{value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Gauge variant ────────────────────────────────────────────────────────────

const GAUGE_SIZE = 120;
const RADIUS = 48;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function GaugeVariant({
  score,
  range,
  factors,
}: {
  score: number;
  range: RangeConfig;
  factors?: ScoreFactors;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const dashOffset = CIRCUMFERENCE * (1 - score / 100);

  // Map range color class to a hex value for SVG stroke
  const STROKE_COLORS: Record<string, string> = {
    'text-danger-500': '#ef4444',
    'text-warning-500': '#f59e0b',
    'text-brand-500': '#6366f1',
    'text-success-500': '#22c55e',
  };
  const strokeColor = STROKE_COLORS[range.color] ?? '#6366f1';

  return (
    <div
      className="relative inline-flex flex-col items-center"
      onMouseEnter={() => factors && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => factors && setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      tabIndex={factors ? 0 : undefined}
    >
      <svg
        width={GAUGE_SIZE}
        height={GAUGE_SIZE}
        role="meter"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Customer identity value score: ${score}`}
        className="block"
      >
        {/* Track */}
        <circle
          cx={GAUGE_SIZE / 2}
          cy={GAUGE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={10}
        />
        {/* Progress */}
        <circle
          cx={GAUGE_SIZE / 2}
          cy={GAUGE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={strokeColor}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${GAUGE_SIZE / 2} ${GAUGE_SIZE / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        {/* Score label */}
        <text
          x={GAUGE_SIZE / 2}
          y={GAUGE_SIZE / 2 - 4}
          textAnchor="middle"
          dominantBaseline="middle"
          className="font-bold"
          style={{ fontSize: 24, fill: strokeColor, fontWeight: 700 }}
        >
          {score}
        </text>
        <text
          x={GAUGE_SIZE / 2}
          y={GAUGE_SIZE / 2 + 16}
          textAnchor="middle"
          style={{ fontSize: 11, fill: '#6b7280' }}
        >
          {range.label}
        </text>
      </svg>

      {factors && showTooltip && (
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 rounded-lg bg-neutral-900 px-3 py-2.5 shadow-lg pointer-events-none"
        >
          <FactorBreakdown factors={factors} />
          <span
            className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900"
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}

// ─── Badge variant ────────────────────────────────────────────────────────────

function BadgeVariant({ score, range }: { score: number; range: RangeConfig }) {
  return (
    <span
      role="meter"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Customer identity value score: ${score}`}
      className={[
        'inline-flex items-center gap-1 rounded-full border px-2.5 h-6 text-xs font-semibold tabular-nums',
        range.bgColor,
        range.textColor,
        range.borderColor,
      ].join(' ')}
    >
      <span className={['w-3 h-3', range.color].join(' ')}>{STAR_ICON}</span>
      {score}
    </span>
  );
}

// ─── Compact variant ──────────────────────────────────────────────────────────

function CompactVariant({ score, range }: { score: number; range: RangeConfig }) {
  return (
    <span
      role="meter"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Customer identity value score: ${score}`}
      className={['inline-flex items-center gap-1.5 text-sm tabular-nums', range.textColor].join(' ')}
    >
      <span className={['w-3.5 h-3.5', range.color].join(' ')}>{STAR_ICON}</span>
      <span className="font-bold">{score}</span>
      <span className="text-neutral-500 font-normal text-xs">{range.label}</span>
    </span>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton({ variant }: { variant: string }) {
  if (variant === 'gauge') {
    return (
      <span
        className="block w-[120px] h-[120px] rounded-full bg-neutral-200 animate-pulse-soft"
        aria-busy="true"
        aria-label="Loading score"
      />
    );
  }
  return (
    <span
      className="block h-6 w-20 rounded-full bg-neutral-200 animate-pulse-soft"
      aria-busy="true"
      aria-label="Loading score"
    />
  );
}

// ─── IdentityValueScore ───────────────────────────────────────────────────────

export function IdentityValueScore({
  score,
  variant = 'compact',
  factors,
  loading = false,
  className,
}: IdentityValueScoreProps) {
  const clampedScore = Math.min(100, Math.max(0, score));
  const range = getRange(clampedScore);

  if (loading) {
    return (
      <span className={['inline-block', className ?? ''].filter(Boolean).join(' ')}>
        <LoadingSkeleton variant={variant} />
      </span>
    );
  }

  return (
    <span className={['inline-block', className ?? ''].filter(Boolean).join(' ')}>
      {variant === 'gauge' && (
        <GaugeVariant score={clampedScore} range={range} factors={factors} />
      )}
      {variant === 'badge' && (
        <BadgeVariant score={clampedScore} range={range} />
      )}
      {variant === 'compact' && (
        <CompactVariant score={clampedScore} range={range} />
      )}
    </span>
  );
}
